import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, unref as _unref, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useUtilStore } from 'stores/useUtilStore';
import { useSettingStore } from 'stores/useSettingStore';
import useLang from 'src/composables/useLang';
import Loading from 'components/common/Loading.vue';
import BottomDialog from 'components/common/BottomDialog.vue';
import CenterDialog from 'components/common/CenterDialog.vue';
import useAudio from 'src/composables/useAudio';
import useMitt, { TEvents } from 'src/composables/useMitt';
import Analyzing from 'components/common/Analyzing.vue';
import { useGeneralStore } from 'stores/useGeneralStore';
import { useAssessmentStore } from 'stores/useAssessmentStore';
import { useAppInterfaceStore } from 'stores/useAppInterfaceStore';
import { appType, tokenResponseType, tokenType } from 'src/types/constType';
import useAlzguardExAuth from 'src/composables/server/useAlzguardExAuth';


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const audio = useAudio();
const mitt = useMitt();
const alzguardExAuth = useAlzguardExAuth();
const generalStore = useGeneralStore();
const utilStore = useUtilStore();
const currentLocale = useI18n().locale;
const settingStore = useSettingStore();
const assessmentStore = useAssessmentStore();
const appInterfaceStore = useAppInterfaceStore();
const { translate } = useLang();

const dialogType = ref('');
const centerDialogTitle = ref(
  translate('errorBottomDialogTitle').replace('\n', '')
);
const centerDialogComment = ref(translate('errorBottomDialogComment'));
const centerDialogButton = ref(translate('errorBottomDialogButton'));
const isNotificationDenied = ref(false);
const isSignInFailed = ref(false);

const isLoading = computed(() => utilStore.getLoading);
const isAnalyzing = computed(() => utilStore.getAnalyzing);
const isErrorBottomOpen = computed(() => utilStore.getErrorBottom);
const isErrorCenterOpen = computed(() => utilStore.getErrorCenter);

function setVh() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

function refresh() {
  dialogType.value = '';
  utilStore.setErrorBottom(false);
  utilStore.setErrorCenter(false);
  if (isSignInFailed.value) router.push('/login');
  else router.go(0);
  isSignInFailed.value = false;
}

function playAudio(src: string) {
  audio.play(src);
}

function playAudioByPath(path: string) {
  audio.play(path, true);
}

watch(
  () => route.path,
  () => {
    if (route.path.includes('/assessment/')) playAudioByPath(route.path);
  }
);

function handleBackButton() {
  const isAssessment = router.currentRoute.value.path.includes('/assessment');
  if (isAssessment) {
    appInterfaceStore.setQuitAssessment(true);
  } else router.go(-1);
}

function openSignInFailedDialog() {
  isSignInFailed.value = true;
  dialogType.value = 'signInFailed';
  centerDialogTitle.value = translate('signInErrorDialogTitle');
  centerDialogComment.value = translate('signInErrorDialogComment');
  centerDialogButton.value = translate('signInErrorDialogButton');
  utilStore.setErrorCenter(true);
}

function interactRequestSignInCallback(callback: number | tokenType) {
  if (typeof callback == 'number') {
    // sign in failed
    if (generalStore.getIsDemo)
      mitt.emit('invalidATCode', undefined); // 데모 -> 유효하지 않은 코드 안내
    else openSignInFailedDialog(); // 로그인 실패 팝업
    utilStore.setLoading(false);
    return;
  }

  // ok
  generalStore.setToken(callback);
  generalStore.getIsDemo ? router.push('/demo/list') : router.push('/at/home');
  utilStore.setLoading(false);
}

async function interactGetTokenCallback(callback: number | tokenType) {
  if (!callback || typeof callback == 'number') {
    // no token data
    if (generalStore.getIsApp) {
      route.meta?.isDemo
        ? await router.push('/demo/login')
        : await router.push('/login');
    } else await router.push('/entry');
    utilStore.setLoading(false);
    return;
  }

  // ok
  const tokenResponse = await alzguardExAuth.manageAlzguardToken(callback);

  switch (tokenResponse) {
    case tokenResponseType.success: // 유효 토큰
    case tokenResponseType.refresh: // at 만료
      if (route.meta.gotoMain) await router.push('/at/home');
      else if (route.meta?.isDemo) await router.push('/demo/list');
      break;
    case tokenResponseType.expired:
      // rt 만료
      await router.push('/expired');
      break;
    default:
      break;
  }
  utilStore.setLoading(false);
}

function interactSetNotificationCallback(callback: number) {
  switch (callback) {
    case -1:
      // ok
      if (isNotificationDenied.value) {
        isNotificationDenied.value = false;
        // 설정 페이지의 경우 알림 설정 값 되돌림
        if (router.currentRoute.value.path.includes('/setting')) {
          appInterfaceStore.setResetNotificationPermission(true);
        }
      }
      break;
    case 1:
      // permission denied
      isNotificationDenied.value = true;
      // 알림 권한 비허용 팝업 발생
      appInterfaceStore.setActiveNotificationAlert(true);
      // 설정 페이지의 경우 알림 설정 값 되돌림
      if (router.currentRoute.value.path.includes('/setting'))
        appInterfaceStore.setResetNotificationPermission(true);
      break;
    case 2:
      // connection error
      // 설정 페이지의 경우 알림 설정 값 되돌림
      if (router.currentRoute.value.path.includes('/setting'))
        appInterfaceStore.setResetNotificationPermission(true);
      break;
    default:
      break;
  }
}

function interactRequestMicCallback(callback: boolean) {
  // 음성 권한 없을 시 팝업 발생
  if (!callback) assessmentStore.setMikeAccessRequested(true);
  else mitt.emit('micAccessInteraction', undefined);
}
function interactAndroidBackButton() {
  // 안드로이드 뒤로가기 버튼 동작
  if (window.alzguardAOS && window.alzguardAOS.androidBackButtonCallback) {
    const isQuit =
      router.currentRoute.value.path.includes('/at') ||
      router.currentRoute.value.path.includes('/demo/login') ||
      router.currentRoute.value.path.includes('/demo/list');
    window.alzguardAOS.androidBackButtonCallback(isQuit);

    handleBackButton();
  }
}

function setCallbackInterfaceAOS() {
  // requestSignIn interface 후 받아오는 callback
  window.alzguardAOS.requestSignInCallback = (callback: number | tokenType) =>
    interactRequestSignInCallback(callback);

  // getTokenCallback interface 후 받아오는 callback
  window.alzguardAOS.getTokenCallback = (callback: number | tokenType) =>
    interactGetTokenCallback(callback);

  // setNotification interface 후 받아오는 callback
  window.alzguardAOS.setNotificationCallback = (callback: number) =>
    interactSetNotificationCallback(callback);

  // web -> app interaction, 앱 마이크 권한 체크
  window.alzguardAOS.requestMicCallback = (callback: boolean) =>
    interactRequestMicCallback(callback);

  // aos 뒤로가기 버튼
  window.alzguardAOS.androidBackButton = () => {
    interactAndroidBackButton();
  };
}

function setCallbackInterfaceIOS() {
  window.alzguardIOS = {
    // requestSignIn interface 후 받아오는 callback
    requestSignInCallback: (callback: number | tokenType) =>
      interactRequestSignInCallback(callback),

    // getTokenCallback interface 후 받아오는 callback
    getTokenCallback: (callback: number | tokenType) =>
      interactGetTokenCallback(callback),

    // setNotification interface 후 받아오는 callback
    setNotificationCallback: (callback: number) =>
      interactSetNotificationCallback(callback),

    // web -> app interaction, 앱 마이크 권한 체크
    requestMicCallback: (callback: boolean) =>
      interactRequestMicCallback(callback),
  };
}

function setCallbackInterface() {
  const userAgent = navigator.userAgent;

  // aos
  if (userAgent.includes('awv')) {
    generalStore.setAppType(appType.aos);
    setCallbackInterfaceAOS();
  }
  // ios
  else if (userAgent.includes('awk')) {
    generalStore.setAppType(appType.ios);
    setCallbackInterfaceIOS();
  }
}

setVh();
window.addEventListener('resize', setVh);
mitt.on('playAudio', (val: TEvents) => {
  playAudio(val as string);
});

onMounted(() => {
  const userAgent = navigator.userAgent;
  const isApp = userAgent.includes('haii.hybrid;');
  generalStore.setIsApp(isApp);
  if (isApp) setCallbackInterface();
  currentLocale.value = settingStore.getSetting.locale ?? 'ko';
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_router_view, { class: "responsive-full-height" }),
    _withDirectives(_createVNode(Loading, null, null, 512), [
      [_vShow, isLoading.value]
    ]),
    _withDirectives(_createVNode(Analyzing, null, null, 512), [
      [_vShow, isAnalyzing.value]
    ]),
    _createVNode(BottomDialog, {
      "single-interaction": "",
      title: _unref(translate)('errorBottomDialogTitle'),
      comment: _unref(translate)('errorBottomDialogComment'),
      button: _unref(translate)('errorBottomDialogButton'),
      dialog: isErrorBottomOpen.value,
      onOnClickButton: refresh
    }, null, 8, ["title", "comment", "button", "dialog"]),
    _createVNode(CenterDialog, {
      "single-interaction": "",
      type: dialogType.value,
      title: centerDialogTitle.value,
      comment: centerDialogComment.value,
      button: centerDialogButton.value,
      dialog: isErrorCenterOpen.value,
      onOnClickButton: refresh
    }, null, 8, ["type", "title", "comment", "button", "dialog"])
  ], 64))
}
}

})